import { useMemo, useState } from 'react';
import { useAtom, useSetAtom } from 'jotai';
import { Checkbox, Button, RadioButtonGroup, RadioButton } from '@carbon/react';
import { Add, Edit, Close, TrashCan } from '@carbon/react/icons';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from 'components/accordion/accordion';
import { format0 } from 'utils/number';
import {
  mapSpatialFilterAtom,
  propertyFiltersAtom,
  colorRulesAtom,
  aoiPolygonAtom,
  aoiEditingAtom,
  type MapSpatialFilter,
} from './atoms';
import { ComparisonColorRules } from './comparison-color-rules';
import { ComparisonColorByModal } from './comparison-color-by-modal';
import { PropertiesFilterModal } from './properties-filter-modal';
import {
  applyPropertyFilters,
  summarizeFilter,
  type FilterKey,
  type PropertyFilter,
} from './property-filters';
import type { ColorRule } from './color-by';
import type { EconomicsPair } from './property-attributes';
import type { ComparisonResult } from './types';
import style from './comparison-sidebar.module.scss';

/** Which main-content sections are visible, toggled from the sidebar. */
export interface LayoutSettings {
  summary: boolean;
  map: boolean;
  properties: boolean;
}

interface ComparisonSidebarProps {
  layout: LayoutSettings;
  onLayoutChange: (next: LayoutSettings) => void;
  /** The comparison results, for color-rule counts and property filtering. */
  results: ComparisonResult[];
  /** Both sides' economics, so Total Target Value is filterable / counted. */
  economics: EconomicsPair | null;
}

// One active filter: name + enable/edit/remove controls, then its value chips
// or range summary.
const FilterCard = ({
  filter,
  results,
  economics,
  onEdit,
  onRemove,
  onToggle,
}: {
  filter: PropertyFilter;
  results: ComparisonResult[];
  economics: EconomicsPair | null;
  onEdit: (filter: PropertyFilter) => void;
  onRemove: (id: string) => void;
  onToggle: (id: string) => void;
}) => {
  const summary = summarizeFilter(filter, results, economics);
  return (
    <li className={style.ruleCard}>
      <div className={style.ruleHeader}>
        <span
          className={`${style.ruleTitle} ${
            filter.enabled ? '' : style.ruleDisabled
          }`}
        >
          {filter.label}
        </span>
        <button
          type="button"
          className={style.ruleAction}
          onClick={() => onToggle(filter.id)}
        >
          {filter.enabled ? 'Disable' : 'Enable'}
        </button>
        <button
          type="button"
          className={style.ruleIcon}
          aria-label="Edit filter"
          onClick={() => onEdit(filter)}
        >
          <Edit size={16} />
        </button>
        <button
          type="button"
          className={style.ruleIcon}
          aria-label="Remove filter"
          onClick={() => onRemove(filter.id)}
        >
          <Close size={16} />
        </button>
      </div>
      <div className={style.chips}>
        {summary.chips.map((chip) => (
          <span key={chip} className={style.chip}>
            {chip}
          </span>
        ))}
      </div>
    </li>
  );
};

/**
 * Left settings sidebar for the comparison results page (mock v16). Hosts Layout
 * Settings, the Global Map Filter with Color-by-Attribute rules, and the
 * Properties filter panel. Color rules recolor the map + tables; property
 * filters narrow the Properties list.
 * @param {ComparisonSidebarProps} props - Layout state and comparison results
 * @return {JSX.Element} The sidebar
 */
export const ComparisonSidebar = ({
  layout,
  onLayoutChange,
  results,
  economics,
}: ComparisonSidebarProps) => {
  const [layoutExpanded, setLayoutExpanded] = useState(true);
  const [mapFilterExpanded, setMapFilterExpanded] = useState(true);
  const [propertiesExpanded, setPropertiesExpanded] = useState(true);
  const [mapFilter, setMapFilter] = useAtom(mapSpatialFilterAtom);
  const [filters, setFilters] = useAtom(propertyFiltersAtom);
  const [, setColorRules] = useAtom(colorRulesAtom);
  // Drawn AOI + its vertex-editing flag, shared with the map's DrawAoiControl.
  // `aoiEditing` is read so the Edit button reflects the active editing state
  // (and stays in sync when the user clicks away on the map → back to false).
  const setAoiPolygon = useSetAtom(aoiPolygonAtom);
  const [aoiEditing, setAoiEditing] = useAtom(aoiEditingAtom);

  // Switch the Global Map Filter mode. Leaving AOI drops any drawn polygon and
  // exits editing, so a stale AOI never lingers under No Filter / Visible Extent.
  const changeMapFilter = (next: MapSpatialFilter) => {
    setMapFilter(next);
    if (next !== 'aoi') {
      setAoiPolygon(null);
      setAoiEditing(false);
    }
  };

  const clearAoi = () => {
    setAoiPolygon(null);
    setAoiEditing(false);
  };

  // Color-by modal (create / edit).
  const [colorModalOpen, setColorModalOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<ColorRule | null>(null);
  // Properties-filter modal (add / edit-focused).
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [filterFocusKey, setFilterFocusKey] = useState<FilterKey | null>(null);

  const setKey = (key: keyof LayoutSettings, checked: boolean) =>
    onLayoutChange({ ...layout, [key]: checked });

  const filteredCount = useMemo(
    () => applyPropertyFilters(results, filters, economics).length,
    [results, filters, economics]
  );
  const totalCount = results.length;
  const propertiesLabel =
    filteredCount === totalCount
      ? `Properties (${format0(totalCount)})`
      : `Properties (${format0(filteredCount)} of ${format0(totalCount)})`;

  const openColorModal = (rule: ColorRule | null) => {
    setEditingRule(rule);
    setColorModalOpen(true);
  };
  const upsertRule = (rule: ColorRule) => {
    setColorRules((prev) => {
      const idx = prev.findIndex((r) => r.id === rule.id);
      if (idx === -1) return [...prev, rule];
      const next = [...prev];
      next[idx] = rule;
      return next;
    });
    setColorModalOpen(false);
  };

  const openFilterModal = (focus: FilterKey | null) => {
    setFilterFocusKey(focus);
    setFilterModalOpen(true);
  };
  const removeFilter = (id: string) =>
    setFilters((prev) => prev.filter((f) => f.id !== id));
  const toggleFilter = (id: string) =>
    setFilters((prev) =>
      prev.map((f) => (f.id === id ? { ...f, enabled: !f.enabled } : f))
    );

  return (
    <div className={style.sidebar}>
      <Accordion
        expanded={layoutExpanded}
        onClick={() => setLayoutExpanded(!layoutExpanded)}
      >
        <AccordionSummary>
          <h3 className={style.sectionTitle}>Layout Settings</h3>
        </AccordionSummary>
        <AccordionDetails>
          <div className={style.checkboxGroup}>
            <Checkbox
              id="layout-setting-summary"
              labelText="Summary"
              checked={layout.summary}
              onChange={(_evt, { checked }: { checked: boolean }) =>
                setKey('summary', checked)
              }
            />
            <Checkbox
              id="layout-setting-map"
              labelText="Map"
              checked={layout.map}
              onChange={(_evt, { checked }: { checked: boolean }) =>
                setKey('map', checked)
              }
            />
            <Checkbox
              id="layout-setting-properties"
              labelText="Properties"
              checked={layout.properties}
              onChange={(_evt, { checked }: { checked: boolean }) =>
                setKey('properties', checked)
              }
            />
          </div>
        </AccordionDetails>
      </Accordion>

      <Accordion
        expanded={mapFilterExpanded}
        onClick={() => setMapFilterExpanded(!mapFilterExpanded)}
      >
        <AccordionSummary>
          <h3 className={style.sectionTitle}>Global Map Filter</h3>
        </AccordionSummary>
        <AccordionDetails>
          <RadioButtonGroup
            name="global-map-filter"
            orientation="vertical"
            valueSelected={mapFilter}
            onChange={(value) => changeMapFilter(value as MapSpatialFilter)}
          >
            <RadioButton
              id="map-filter-none"
              labelText="No Map Filter"
              value="none"
            />
            <RadioButton
              id="map-filter-extent"
              labelText="Use Visible Map Extent"
              value="extent"
            />
            <RadioButton
              id="map-filter-aoi"
              labelText="Draw AOI Polygon"
              value="aoi"
            />
          </RadioButtonGroup>

          {/* Applied AOI — shown whenever Draw AOI Polygon is selected (mock v16).
              Edit (blue) enters vertex editing on the map; Clear (red) removes the
              polygon so the map re-arms a fresh draw. */}
          {mapFilter === 'aoi' && (
            <div className={style.appliedAoi}>
              <span className={style.appliedAoiLabel}>AOI Polygon</span>
              <Button
                kind="ghost"
                size="sm"
                hasIconOnly
                isSelected={aoiEditing}
                renderIcon={Edit}
                iconDescription={aoiEditing ? 'Done editing AOI' : 'Edit AOI'}
                tooltipPosition="bottom"
                onClick={() => setAoiEditing((editing) => !editing)}
              />
              <Button
                kind="danger--ghost"
                size="sm"
                hasIconOnly
                renderIcon={TrashCan}
                iconDescription="Clear AOI"
                tooltipPosition="bottom"
                onClick={clearAoi}
              />
            </div>
          )}

          <Button
            className={style.actionButton}
            kind="tertiary"
            size="sm"
            renderIcon={Add}
            onClick={() => openColorModal(null)}
          >
            Color by Attribute
          </Button>

          <ComparisonColorRules results={results} onEdit={openColorModal} />
        </AccordionDetails>
      </Accordion>

      <Accordion
        expanded={propertiesExpanded}
        onClick={() => setPropertiesExpanded(!propertiesExpanded)}
      >
        <AccordionSummary>
          <h3 className={style.sectionTitle}>{propertiesLabel}</h3>
        </AccordionSummary>
        <AccordionDetails>
          <Button
            className={style.actionButton}
            kind="tertiary"
            size="sm"
            renderIcon={Add}
            onClick={() => openFilterModal(null)}
          >
            Add Filter
          </Button>
          {filters.length === 0 ? (
            <p className={style.emptyState}>No filters added.</p>
          ) : (
            <ul className={style.ruleList}>
              {filters.map((filter) => (
                <FilterCard
                  key={filter.id}
                  filter={filter}
                  results={results}
                  economics={economics}
                  onEdit={(f) => openFilterModal(f.key)}
                  onRemove={removeFilter}
                  onToggle={toggleFilter}
                />
              ))}
            </ul>
          )}
        </AccordionDetails>
      </Accordion>

      <ComparisonColorByModal
        open={colorModalOpen}
        onClose={() => setColorModalOpen(false)}
        results={results}
        editingRule={editingRule}
        onApply={upsertRule}
      />
      <PropertiesFilterModal
        open={filterModalOpen}
        onClose={() => setFilterModalOpen(false)}
        results={results}
        filters={filters}
        economics={economics}
        focusKey={filterFocusKey}
        onApply={setFilters}
      />
    </div>
  );
};
